import React, { useCallback, useEffect, useState } from 'react';
import { Save, Plus, Trash2, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { availabilityApi, catalogApi } from '../lib/api';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const card = 'bg-white rounded-2xl border border-slate-200 p-4';
const err = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? (d[0]?.msg || 'Valeurs invalides').replace('Value error, ', '') : d || 'Erreur';
};
const frDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

const SettingsCard = ({ settings, setSettings, onSave }) => {
  const setDay = (d, k, v) => setSettings({ ...settings, week: { ...settings.week, [d]: { ...settings.week[d], [k]: v } } });
  const num = (k) => (e) => setSettings({ ...settings, [k]: Number(e.target.value) });
  return (
    <section className={card} data-testid="availability-settings">
      <h2 className="font-bold text-slate-900 mb-3">Horaires de rendez-vous</h2>
      <div className="space-y-2">
        {DAYS.map((label, i) => {
          const d = settings.week[String(i)];
          return (
            <div key={label} className="grid grid-cols-[6rem_1fr_auto_1fr] items-center gap-2" data-testid={`day-row-${i}`}>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={d.open} onChange={(e) => setDay(i, 'open', e.target.checked)} data-testid={`day-open-${i}`} />{label}
              </label>
              <Input type="time" value={d.start} disabled={!d.open} onChange={(e) => setDay(i, 'start', e.target.value)} className="w-full min-w-0 px-2" data-testid={`day-start-${i}`} />
              <span className="text-slate-400">à</span>
              <Input type="time" value={d.end} disabled={!d.open} onChange={(e) => setDay(i, 'end', e.target.value)} className="w-full min-w-0 px-2" data-testid={`day-end-${i}`} />
            </div>
          );
        })}
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
        <label>Délai minimum (heures)<Input type="number" value={settings.min_notice_hours} onChange={num('min_notice_hours')} data-testid="input-min-notice" /></label>
        <label>Réservable jusqu'à (jours)<Input type="number" value={settings.horizon_days} onChange={num('horizon_days')} data-testid="input-horizon" /></label>
        <label>Pas des créneaux
          <select className="w-full h-10 rounded-md border border-input px-3" value={settings.slot_step_minutes} onChange={num('slot_step_minutes')} data-testid="select-step">
            {[15, 30, 60].map((v) => <option key={v} value={v}>{v} min</option>)}
          </select>
        </label>
      </div>
      <p className="text-xs text-slate-500 mt-2">Fuseau horaire : {settings.timezone}. Jours fériés français exclus automatiquement.</p>
      <Button onClick={onSave} className="mt-3 gap-1.5" data-testid="save-settings-button"><Save className="w-4 h-4" />Enregistrer</Button>
    </section>
  );
};

const EMPTY_UNAV = { date_from: '', date_to: '', start_time: '', end_time: '', reason: '' };

const UnavailabilityCard = ({ items, onAdd, onRemove }) => {
  const [f, setF] = useState(EMPTY_UNAV);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    const ok = await onAdd({ ...f, date_to: f.date_to || f.date_from, start_time: f.start_time || null, end_time: f.end_time || null });
    if (ok) setF(EMPTY_UNAV);
  };
  return (
    <section className={card} data-testid="unavailabilities">
      <h2 className="font-bold text-slate-900 mb-3">Indisponibilités</h2>
      <div className="grid sm:grid-cols-5 gap-2 text-sm">
        <Input type="date" value={f.date_from} onChange={set('date_from')} data-testid="unav-from" />
        <Input type="date" value={f.date_to} onChange={set('date_to')} data-testid="unav-to" />
        <Input type="time" value={f.start_time} onChange={set('start_time')} data-testid="unav-start" />
        <Input type="time" value={f.end_time} onChange={set('end_time')} data-testid="unav-end" />
        <Input placeholder="Motif (congés…)" value={f.reason} onChange={set('reason')} data-testid="unav-reason" />
      </div>
      <p className="text-xs text-slate-500 mt-1">Sans heures : journée(s) entière(s).</p>
      <Button size="sm" onClick={submit} disabled={!f.date_from} className="mt-2 gap-1" data-testid="unav-add-button"><Plus className="w-4 h-4" />Ajouter</Button>
      <ul className="mt-3 divide-y divide-slate-100">
        {items.map((u) => (
          <li key={u.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`unav-${u.id}`}>
            <span className="flex-1">{frDate(u.date_from)}{u.date_to !== u.date_from && ` → ${frDate(u.date_to)}`}
              {' · '}{u.start_time ? `${u.start_time}–${u.end_time}` : 'journée entière'}{u.reason && ` · ${u.reason}`}</span>
            <Button size="sm" variant="ghost" onClick={() => onRemove(u.id)} data-testid={`unav-remove-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
          </li>
        ))}
        {items.length === 0 && <li className="py-2 text-sm text-slate-400">Aucune indisponibilité.</li>}
      </ul>
    </section>
  );
};

const PreviewCard = ({ services, reloadKey }) => {
  const [code, setCode] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!code) return;
    availabilityApi.preview(code).then((r) => { setData(r.data); setError(''); }).catch((e) => { setData(null); setError(err(e)); });
  }, [code, reloadKey]);
  return (
    <section className={card} data-testid="availability-preview">
      <h2 className="font-bold text-slate-900 mb-1 flex items-center gap-2"><CalendarDays className="w-4 h-4" />Aperçu du calendrier (14 jours)</h2>
      <p className="text-xs text-slate-500 mb-3">Aperçu réservé à l'administration : aucune réservation n'est ouverte aux clients.</p>
      <select className="w-full max-w-md h-10 rounded-md border border-input px-3 text-sm mb-3" value={code} onChange={(e) => setCode(e.target.value)} data-testid="preview-service-select">
        <option value="">Choisir une prestation…</option>
        {services.map((s) => <option key={s.code} value={s.code}>{s.name}{s.duration_minutes ? ` (${s.duration_minutes} min)` : ' (durée non renseignée)'}</option>)}
      </select>
      {error && <p className="text-sm text-amber-700" data-testid="preview-error">{error}</p>}
      {data && (
        <>
          <p className="text-xs text-slate-500 mb-2" data-testid="preview-duration">Durée : {data.duration_minutes} min · temps tampon : {data.buffer_minutes} min</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.days.map((d) => (
              <div key={d.date} className="border border-slate-200 rounded-xl p-2" data-testid={`preview-day-${d.date}`}>
                <p className="text-sm font-semibold capitalize">{frDate(d.date)}</p>
                {d.slots.length ? (
                  <div className="flex flex-wrap gap-1 mt-1">{d.slots.map((s) => <span key={s.time} className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">{s.time}</span>)}</div>
                ) : <p className="text-xs text-slate-400 mt-1">{d.closed_reason || 'Aucun créneau (délai ou indisponibilité)'}</p>}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const AvailabilityAdmin = () => {
  const [settings, setSettings] = useState(null);
  const [unav, setUnav] = useState([]);
  const [services, setServices] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const [s, u, c] = await Promise.all([availabilityApi.settings(), availabilityApi.listUnav(), catalogApi.list('services')]);
      setSettings(s.data); setUnav(u.data); setServices(c.data);
    } catch { toast.error('Erreur de chargement des disponibilités'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { setReloadKey((n) => n + 1); return availabilityApi.listUnav().then((r) => setUnav(r.data)); };
  const save = async () => {
    try {
      const { week, min_notice_hours, horizon_days, slot_step_minutes } = settings;
      setSettings((await availabilityApi.saveSettings({ week, min_notice_hours, horizon_days, slot_step_minutes })).data);
      setReloadKey((n) => n + 1); toast.success('Horaires enregistrés');
    } catch (e) { toast.error(err(e)); }
  };
  const add = async (payload) => {
    try { await availabilityApi.addUnav(payload); await refresh(); toast.success('Indisponibilité ajoutée'); return true; }
    catch (e) { toast.error(err(e)); return false; }
  };
  const remove = async (id) => {
    try { await availabilityApi.removeUnav(id); await refresh(); toast.success('Indisponibilité retirée'); }
    catch (e) { toast.error(err(e)); }
  };

  if (!settings) return <p className="text-sm text-slate-500">Chargement…</p>;
  return (
    <div className="space-y-4" data-testid="availability-admin">
      <div className="grid lg:grid-cols-2 gap-4">
        <SettingsCard settings={settings} setSettings={setSettings} onSave={save} />
        <UnavailabilityCard items={unav} onAdd={add} onRemove={remove} />
      </div>
      <PreviewCard services={services} reloadKey={reloadKey} />
    </div>
  );
};

export default AvailabilityAdmin;
